import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Filter } from '@/components/Filters/Filter';
import {
  DEFAULT_VALUES,
  FILTERS
} from '@/components/Filters/Filters.constants';
import { ParticipantTabs } from '@/components/ParticipantTabs/ParticipantTabs';
import { Button } from '@/components/ui/Button/Button';
import { CrossIcon } from '@/components/ui/Icons/Icons';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { FilterTypeEnum } from '@/constants/common';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { FormValues, FormValuesKeys } from '@/types';
import { hasFormValuesChanged } from '@/utils/helpers/hasFormValuesChanged';

import * as S from './Filters.styles';

const id = 'filterFormId';

interface FiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: FormValues) => void;
  onReset?: () => void;
  externalMethods?: UseFormReturn<FormValues>;
}

function isExistingFormField(field: string): field is FormValuesKeys {
  const formKeys: FormValuesKeys[] = Object.keys(
    DEFAULT_VALUES
  ) as FormValuesKeys[];
  return formKeys.includes(field as FormValuesKeys);
}

export const Filters: FC<FiltersProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onReset,
  externalMethods
}) => {
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const { t } = useTranslation();

  const isTablet = useMediaQuery('tablet');
  const isMobile = useMediaQuery('mobile');

  const internalMethods = useForm({
    defaultValues: DEFAULT_VALUES
  });

  const methods = externalMethods ?? internalMethods;

  const keys: FormValuesKeys[] = FILTERS.map(item => item.formField).flat(1);
  const formValues = methods.watch(keys);
  const hasValues = hasFormValuesChanged(formValues, DEFAULT_VALUES, keys);

  const { handleSubmit, setValue, reset } = methods;

  const submitCallback = async (data: FormValues) => {
    FILTERS.forEach(item => {
      if (item.type !== FilterTypeEnum.RANGE) return;
      const [fromField, toField] = item.formField;

      if (!isExistingFormField(fromField) || !isExistingFormField(toField))
        return;

      const fromValue = (data[fromField] || 0) as number;
      const toValue = (data[toField] || 0) as number;

      if (fromValue > toValue) {
        // eslint-disable-next-line no-param-reassign
        data = { ...data, [toField]: fromValue };
        setValue(toField, fromValue);
      }
    });

    onSubmit?.(data);
  };

  const handleCloseFilter = () => {
    onClose();
    reset();
  };

  const handleResetFilter = () => {
    onReset?.();
    reset();
  };

  const toggleFilter = (key: string) => {
    setOpenFilterKey(prev => (prev === key ? null : key));
  };

  useEffect(() => {
    if (isOpen) return;
    setOpenFilterKey(null);
  }, [isOpen]);

  return (
    <>
      {isOpen && <Overlay onClick={onClose} zIndex={10} />}
      <S.Wrapper $isOpen={isOpen} onClick={e => e.stopPropagation()}>
        <S.HeaderWrapper>
          <S.Header>
            <S.CloseButton onClick={handleCloseFilter}>
              <CrossIcon />
            </S.CloseButton>

            <S.FilterTitle variant={isTablet ? 'h5' : 'h4'}>
              {t('Filter.title')}
            </S.FilterTitle>

            {!!isTablet && (
              <S.RightSection $isVisible={!!hasValues}>
                <Button variant="link" onClick={handleResetFilter}>
                  <Typography variant="link_15">
                    {t('Filter.reset_button')}
                  </Typography>
                </Button>
              </S.RightSection>
            )}
          </S.Header>

          {(isTablet || isMobile) && <ParticipantTabs />}
        </S.HeaderWrapper>

        <S.Content>
          <FormProvider {...methods}>
            <form id={id} onSubmit={handleSubmit(submitCallback)}>
              {isOpen &&
                FILTERS.map(
                  ({ name, children, formField, type, queryField }) => (
                    <Filter
                      queryField={queryField}
                      key={name}
                      title={name}
                      formField={formField}
                      type={type}
                      isOpen={openFilterKey === name}
                      onToggle={() => toggleFilter(name)}
                    >
                      {children}
                    </Filter>
                  )
                )}
            </form>
          </FormProvider>
        </S.Content>

        <S.Footer>
          {/* Hide cancel button on screen < tablet */}
          {!isTablet && (
            <Button variant="secondary" onClick={handleResetFilter}>
              {t('Filter.reset_button')}
            </Button>
          )}

          <Button
            form={id}
            type="submit"
            variant={isTablet ? 'primary' : 'brand'}
            fullWidth
          >
            {t('Filter.confirm_button')}
          </Button>
        </S.Footer>
      </S.Wrapper>
    </>
  );
};
